<script>
    import {onMount} from 'svelte';
    import {t} from '$utils/lang.js';
    import { devLog as _devLog } from '$utils/devLog.js';
    import { loadStripeJs } from '$lib/stripeLoader.js';
    const devLog = (tag, ...args) => _devLog(`StripePayment:${tag}`, ...args);

    // reference: [1]
    let {
        paymentId,
        metadata,
        canOrder = true,
        buyBtnClass = '',
        cardClass = '',
        onApproved = () => {},
    } = $props();

    let isLoading = $state(true);
    let paying = $state(false);
    let errorMessage = $state(null);
    let saveData = $state(false);
    let containerEl;
    let paymentElement = null;
    let stripe = $state.raw(null);
    let elements = $state.raw(null);

    // Link ("save my data for faster checkout") is Stripe's optional block with e-mail, phone and name. It stays off until the
    // customer ticks the box; Stripe cannot show it on demand, so the element is rebuilt (card data typed so far is lost, which
    // is fine: the box sits above the pay button, not between the fields).
    function createElement() {
        paymentElement = elements.create('payment', {layout: 'tabs', wallets: {link: saveData ? 'auto' : 'never'}});
        paymentElement.mount(containerEl);
        paymentElement.on('ready', () => { isLoading = false; });
    }

    function toggleSaveData() {
        if (!elements) return;
        isLoading = true;
        try { paymentElement?.destroy(); } catch { /* noop */ }
        createElement();
    }

    // onMount, not $effect: a refetch of `metadata` must not open a second PaymentIntent.
    onMount(() => {
        let destroyed = false;

        (async () => {
            try {
                const params = new URLSearchParams({cl: 'm7-checkout-dpc', fnc: 'createGatewayOrder', payment_id: paymentId});
                const res = await fetch(`/index.php?${params}`, {method: 'POST', headers: {'Content-Type': 'application/json'}});
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data?.clientSecret) throw new Error(data?.message ?? 'createGatewayOrder');
                await loadStripeJs();
                if (destroyed) return;

                stripe = window.Stripe(data.publishableKey || metadata.publishableKey, {locale: metadata.locale ?? 'auto'});
                elements = stripe.elements({clientSecret: data.clientSecret, locale: metadata.locale ?? 'auto'});
                createElement();
                devLog('mounted', {paymentId, intentId: data.intentId});
            } catch (err) {
                if (destroyed) return;
                isLoading = false;
                errorMessage = t('Die Zahlung konnte nicht geladen werden. Bitte Seite neu laden.');
                devLog('init:error', {error: err.message});
            }
        })();

        return () => {
            destroyed = true;
            try { paymentElement?.destroy(); } catch { /* noop */ }
        };
    });

    async function pay() {
        if (!stripe || !elements || paying) return;
        paying = true;
        errorMessage = null;
        // Redirect methods (Przelewy24, Klarna, some BLIK flows) leave the page and come back to stripeReady=1,
        // which Order.svelte picks up. All others finish here.
        const returnUrl = `${window.location.origin}${window.location.pathname}?stripeReady=1${window.location.hash}`;
        const {error, paymentIntent} = await stripe.confirmPayment({
            elements,
            confirmParams: {return_url: returnUrl},
            redirect: 'if_required',
        });
        if (error) {
            devLog('confirm:error', {type: error.type, code: error.code});
            errorMessage = error.message || t('Die Zahlung wurde nicht abgeschlossen. Bitte versuche es erneut oder wähle eine andere Zahlungsart.');
            paying = false;
            return;
        }
        const accepted = ['succeeded', 'processing'].includes(paymentIntent?.status)
            || paymentIntent?.next_action?.type === 'display_bank_transfer_instructions';
        devLog('confirm:done', {status: paymentIntent?.status, accepted});
        if (accepted) {
            onApproved();
            return;
        }
        errorMessage = t('Die Zahlung wurde nicht abgeschlossen. Bitte versuche es erneut oder wähle eine andere Zahlungsart.');
        paying = false;
    }
</script>

<div class="mt-3 flex flex-col gap-3" data-checkout-section="stripe-payment" data-payment-id={paymentId}>
    <div class={cardClass}>
        <div bind:this={containerEl}>
            {#if isLoading}
                <div class="h-[120px] bg-gray-100 animate-pulse rounded"></div>
            {/if}
        </div>

        <label class="flex items-start gap-2 text-sm cursor-pointer" data-checkout-section="stripe-save-data">
            <input type="checkbox" class="mt-0.5" bind:checked={saveData} onchange={toggleSaveData} data-checkout-input="stripe-save-data">
            <span>{t('Meine Daten speichern für eine schnellere Kaufabwicklung')}</span>
        </label>

        {#if errorMessage}
            <p class="text-scarlet text-sm" data-checkout-section="stripe-error">{errorMessage}</p>
        {/if}
    </div>

    <button
        class={buyBtnClass}
        disabled={!canOrder || isLoading || paying || !!(errorMessage && !stripe)}
        class:opacity-50={!canOrder || isLoading || paying}
        class:cursor-not-allowed={!canOrder || isLoading || paying}
        onclick={pay}
        type="button"
        data-checkout-action="place-order"
        aria-disabled={!canOrder || isLoading || paying}
    >
        {paying ? t('Zahlung wird geprüft…') : t('Jetzt kaufen')}
    </button>
</div>
